import { textListPrefixFromProjection } from './canvas-agent/textListPrefixRead';
import {
  CANVAS_DOCUMENT_READ_CONTENT_MAX_LENGTH,
  CANVAS_INSPECT_BLOCK_LIMIT_DEFAULT,
  CANVAS_INSPECT_BLOCK_LIMIT_MAX,
  CANVAS_INSPECT_PUBLIC_RANGE_LIMIT_MAX,
  cleDeMontage,
  type CanvasConversationActionEnvelope,
  type CanvasConversationInspectEnvelope,
  type CanvasCapabilityQualification,
  type CanvasEmptyDocumentProof,
  type CanvasFormatRead,
  type CanvasFormatReadingScope,
  type CanvasInspectionRead,
  type CanvasInspectionTextContinuation,
  type CanvasTextSearchMiss,
  type CanvasInspectionProjection,
  type CanvasSelector,
  type CanvasSuperDocResultPayload,
} from '@hector/shared-types';
import type { SuperDocInstance } from '@superdoc/react';
import { lireLaRevisionDuDocument, marquerRevisionDeLAgent, REVISION_ABSENTE } from './canvas-agent/fencesDeMutation';
import { ledgerForAgentTrackedInsertions } from './canvas-agent/agentTrackedInsertionLedger';
import {
  isCanvasSnapshotReadError,
  lireLesEnTetes,
  readCanvasSnapshot,
  type CanvasSnapshot,
} from './canvas-agent/canvasSnapshot';
import {
  CanvasProjectionReadError,
  isCanvasProjectionReadError,
  lireProjectionMarkdown,
  toModelSafeCanvasProjectionBlocks,
  type CanvasMarkdownProjection,
  type CanvasProjectionBlock,
} from './canvas-agent/canvasProjection';
import { listerLesImages, nommerLesImagesDeLaProjection, type ImageListee } from './canvas-agent/imagesDuCorps';
import { listerLesBlocsApparies } from './canvas-agent/selectorResolver';
import { ExportDeLActeImpossible, exporterLActeEnMemoire } from './canvas-agent/exportDeLActe';
import { formePourLAgent, LieuDeFormeAbsent } from './canvas-agent/formePourLAgent';
import { lectureAvecFormePourLAgent, type LectureAvecFormeDeLActe } from './canvas-agent/lectureAvecFormePourLAgent';
import { LectureDeFormeImpossible, lireLaForme, RangHorsDuDocument } from '@hector/docx-forme';
import { CANVAS_ACTION_EXECUTORS } from './canvas-agent/canvasActionRegistry';
import { CONTROLE_DU_RETRAIT_DES_BORDURES } from './canvas-agent/formatActionExecutors';
import { abortedBeforeMutation, inconclusiveAfterMutation, toCanvasSnapshotSummary, type CanvasActionReceipt } from './canvas-agent/actionReceipt';
import {
  creerVerificateurDEpoque,
  estEpoqueDepassee,
  type JetonDEpoque,
} from './canvas-agent/executionAsynchrone';
import type { SuperDocDocumentApi } from './canvas-agent/superDocDocumentApi';
import type { CanvasActionContext } from './canvas-agent/textActionExecutors';
import {
  lireLesCapacites,
  messageDeCapaciteAbsente,
  messageDeCapacitesNonQualifiees,
  messageDeSuiviImpossible,
  etapesPlanNativesManquantes,
  operationsManquantes,
  operationsNonSuivables,
  qualifierLesCapacitesDuDocument,
} from './canvas-agent/capacitesDuDocument';
import {
  CODE_ENREGISTREMENT_EN_COURS,
  creerReessaiSurRefusPassager,
  decrireLExportEnCours,
  type BilanDesRefusPassagers,
} from './canvas-agent/refusPassager';
import { captureCanvasAgentTransportDiagnostic } from '../diagnostics/canvasWriteDiagnostics';
import { lireLeModeDuDocument } from './modeDuDocument';
import type { PeintreDuDocument } from './canvas-agent/peintreDeLEditeur';
import type { CanvasDocumentWriter, CanvasNamedLetterheadResolver } from './canvasDocumentScope';
import { normalizeSearchText } from '@hector/agent-text-normalizer';

/**
 * L'enveloppe agent en forme conversation, la seule que ce navigateur exécute
 * depuis #1336. La garde amont (`canvasAgentRequestGuard`) l'a déjà validée
 * contre son schéma `.strict()` : rien ici ne relit une identité.
 */
type CanvasRuntimeRequest = CanvasConversationInspectEnvelope | CanvasConversationActionEnvelope;

interface SuperDocEditorWithDocumentApi {
  doc?: SuperDocDocumentApi;
}

/**
 * Ce que l'appelant garantit sur l'identité du document (#939).
 *
 * `attendue` est capturée **au début du callback**, `lireCourante` relit
 * l'époque réellement montée au moment où on la demande — jamais une valeur
 * capturée à la fermeture, qui serait aussi périmée que ce qu'elle prétend
 * vérifier.
 */
export interface GardeDEpoque {
  attendue: JetonDEpoque;
  lireCourante: () => JetonDEpoque | null;
}

/**
 * L'instantané qu'on met dans un receipt de refus PRÉALABLE.
 *
 * Un refus avant mutation n'a pas lu le document — le faire lire pour remplir ce
 * champ coûterait un aller-retour pour une information dont personne ne se sert
 * dans ce cas. `'unknown'` est la sentinelle de révision absente, la même que
 * `REVISION_ABSENTE` : un refus, jamais une écriture au mauvais endroit.
 */
const SNAPSHOT_INDISPONIBLE = toCanvasSnapshotSummary({
  revision: 'unknown',
  counts: { blocks: 0, trackedChanges: 0, comments: 0 },
});

function actionName(request: CanvasRuntimeRequest): string {
  return request.tool === 'canvas_inspect' ? 'inspect' : request.action.action;
}

function receiptOk(
  request: CanvasRuntimeRequest,
  data?: unknown,
  options: {
    capabilityQualification?: CanvasCapabilityQualification;
    emptyDocumentProof?: CanvasEmptyDocumentProof;
    inspectionRead?: CanvasInspectionRead;
    inspectionTextContinuations?: readonly CanvasInspectionTextContinuation[];
    formatReadingScope?: CanvasFormatReadingScope;
    textSearchMiss?: CanvasTextSearchMiss;
  } = {},
): CanvasSuperDocResultPayload {
  return {
    requestId: request.requestId,
    tool: request.tool,
    action: actionName(request),
    ok: true,
    ...(data === undefined ? {} : { data }),
    ...(options.capabilityQualification === undefined ? {} : { capabilityQualification: options.capabilityQualification }),
    ...(options.emptyDocumentProof === undefined ? {} : { emptyDocumentProof: options.emptyDocumentProof }),
    ...(options.inspectionRead === undefined ? {} : { inspectionRead: options.inspectionRead }),
    ...(options.textSearchMiss === undefined ? {} : { textSearchMiss: options.textSearchMiss }),
    ...(options.inspectionTextContinuations === undefined || options.inspectionTextContinuations.length === 0
      ? {}
      : { inspectionTextContinuations: options.inspectionTextContinuations }),
    ...(options.formatReadingScope === undefined ? {} : { formatReadingScope: options.formatReadingScope }),
  } as CanvasSuperDocResultPayload;
}

function receiptError(
  request: CanvasRuntimeRequest,
  error: string,
  options: { code?: string; capability?: string; data?: unknown } = {},
): CanvasSuperDocResultPayload {
  return {
    requestId: request.requestId,
    tool: request.tool,
    action: actionName(request),
    ok: false,
    error,
    ...options,
  } as CanvasSuperDocResultPayload;
}

function requireEditor(instance: SuperDocInstance | null): SuperDocEditorWithDocumentApi | null {
  return (instance as unknown as { activeEditor?: SuperDocEditorWithDocumentApi } | null)?.activeEditor ?? null;
}

/**
 * SuperDoc can finish a projection just before its asynchronous review window
 * catches up with a write. These waits are the already-qualified live cadence
 * from `mesure-carte-de-regime.spec.ts`; they are a bounded convergence retry,
 * never a way to accept a projection from another document revision (#1605).
 */
const PROJECTION_FENCE_RETRY_DELAYS_MS = [0, 75, 200, 500, 1_000] as const;

function attendre(milliseconds: number): Promise<void> {
  return milliseconds === 0 ? Promise.resolve() : new Promise((resolve) => setTimeout(resolve, milliseconds));
}

function blocsProjectionQuiApparientTexte(
  blocks: readonly CanvasProjectionBlock[],
  selector: Extract<CanvasSelector, { kind: 'textSearch' }>,
): CanvasProjectionBlock[] {
  const termes = selector.terms.map(normalizeSearchText);
  if (termes.some((terme) => terme.length === 0)) return [];
  return blocks.filter((block) => {
    const texte = normalizeSearchText(block.fullText);
    return selector.match === 'any'
      ? termes.some((terme) => texte.includes(terme))
      : termes.every((terme) => texte.includes(terme));
  });
}

/**
 * Ce qu'une recherche à plusieurs termes sans résultat doit dire : combien de blocs portent chaque terme seul.
 * `undefined` hors de ce cas — un seul terme, `match:"any"`, un terme vide, ou une recherche qui a trouvé.
 */
function rechercheSansBlocCommun(
  blocks: readonly CanvasProjectionBlock[],
  selector: CanvasSelector | undefined,
  apparies: readonly CanvasProjectionBlock[] | undefined,
): CanvasTextSearchMiss | undefined {
  if (selector?.kind !== 'textSearch' || selector.match === 'any' || selector.terms.length < 2 || apparies?.length !== 0) return undefined;
  const textes = blocks.map((block) => normalizeSearchText(block.fullText));
  const blocksHoldingEachTerm = selector.terms.map((term) => {
    const terme = normalizeSearchText(term);
    return { term, blocks: terme.length === 0 ? 0 : textes.filter((texte) => texte.includes(terme)).length };
  });
  return { match: 'all', blocksHoldingEachTerm };
}

/**
 * Resolves selectors in the requested projection, not in the raw block list.
 *
 * Ordinals are the one legacy exception: their visible numbering belongs to
 * `blocks.list()`, so it first resolves to an existing block id and then keeps
 * only the native projection entry with that exact public identity. No text,
 * ordinal or nearby-offset join can manufacture an address in a projection.
 */
async function blocsProjectionQuiApparientSelecteur(
  doc: SuperDocDocumentApi,
  projection: CanvasMarkdownProjection,
  selector: CanvasSelector,
): Promise<CanvasProjectionBlock[]> {
  const blocks = projection.blocks;
  if (selector.kind === 'nodeId') {
    return blocks.filter((block) => block.identity === 'public' && block.nodeId === selector.nodeId);
  }
  if (selector.kind === 'textSearch') {
    if (!projection.hasCompleteTopLevelBlockMap) {
      throw new CanvasProjectionReadError(
        'projection_malformed',
        'canvas_inspect cannot resolve a targeted textSearch because SuperDoc did not return a complete top-level block map. '
        + 'The readable document fallback is not a localizable selector result and would otherwise disclose an unrelated leading excerpt. '
        + 'Run a non-targeted inspection only to read that fallback, then retry targeting after SuperDoc returns a complete native block map.',
      );
    }
    return blocsProjectionQuiApparientTexte(blocks, selector);
  }

  const rawMatches = await listerLesBlocsApparies(doc, selector);
  const publicIds = new Set(rawMatches.map((block) => block.nodeId));
  return blocks.filter((block) => block.identity === 'public' && publicIds.has(block.nodeId));
}

type InspectionProjeteeStable = {
  snapshot: CanvasSnapshot;
  projection: CanvasMarkdownProjection;
  apparies?: CanvasProjectionBlock[];
  emptyDocumentProof?: CanvasEmptyDocumentProof;
};

function estLectureDeDocument(input: CanvasConversationInspectEnvelope['input']): boolean {
  return input.selector === undefined
    && input.blockOffset === undefined
    && input.blockLimit === undefined
    && input.textWindow === undefined
    // Une lecture de forme ne porte ni sélecteur ni fenêtre, mais elle n'est
    // pas une lecture du document : elle rend la forme, pas le texte de l'acte.
    && input.formatOf === undefined
    // La lecture avec forme rend le texte, mais sans repère ni portée : elle n'arme aucune retouche.
    && input.formatReading === undefined;
}

function fenetreDeBlocs(input: CanvasConversationInspectEnvelope['input']): { offset: number; limit: number } {
  return {
    offset: input.blockOffset ?? 0,
    limit: input.blockLimit ?? CANVAS_INSPECT_BLOCK_LIMIT_DEFAULT,
  };
}

function fenetreDuSnapshot(input: CanvasConversationInspectEnvelope['input']): { offset: number; limit: number } {
  // Une lecture de forme ne publie aucun bloc : l'instantané n'y sert qu'à la
  // révision et aux comptes, la plus petite fenêtre suffit.
  if (input.formatOf !== undefined || input.formatReading !== undefined) return { offset: 0, limit: 1 };
  return estLectureDeDocument(input)
    ? { offset: 0, limit: CANVAS_INSPECT_BLOCK_LIMIT_MAX }
    : fenetreDeBlocs(input);
}

function estEnregistrement(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * A discovery response can prove absence only in its documented envelope. A
 * filtered, paginated, malformed, or failed response remains useful to the
 * normal model snapshot, but it must never initialise a supposedly empty body.
 */
function prouveZeroDeDecouverte(source: unknown, revision: string): boolean {
  if (!estEnregistrement(source) || source.success === false || source.evaluatedRevision !== revision || source.total !== 0) {
    return false;
  }
  if (!Array.isArray(source.items) || source.items.length !== 0 || !estEnregistrement(source.page)) return false;
  return source.page.offset === 0
    && source.page.returned === 0
    && typeof source.page.limit === 'number'
    && Number.isInteger(source.page.limit)
    && source.page.limit >= 1;
}

function formeDeCorpsVideInitial(
  input: CanvasConversationInspectEnvelope['input'],
  snapshot: CanvasSnapshot,
  projection: CanvasMarkdownProjection,
): CanvasEmptyDocumentProof['body'] | undefined {
  const preconditionsCommunes = estLectureDeDocument(input)
    && snapshot.revision !== REVISION_ABSENTE
    && snapshot.counts.blocksTotalExact
    && projection.projection.mode === 'final'
    && projection.projection.status === 'success'
    && !projection.projection.lossy
    && projection.content === '';
  if (!preconditionsCommunes) return undefined;

  if (snapshot.counts.blocks === 0 && snapshot.blocks.length === 0) {
    return { kind: 'zero_blocks' };
  }
  const [seulBloc] = snapshot.blocks;
  if (
    snapshot.counts.blocks === 1
    && snapshot.blocks.length === 1
    && seulBloc?.nodeType === 'paragraph'
    && seulBloc.fullText === ''
    && seulBloc.text === ''
    && seulBloc.textTruncated === undefined
  ) {
    return { kind: 'single_empty_paragraph', nodeId: seulBloc.nodeId };
  }
  return undefined;
}

/**
 * `blocks.list()` is text-oriented. The public `doc.get({})` SDM body is the
 * authority for the physical Word placeholder: it must contain no body node,
 * or exactly one paragraph with zero inline nodes. A run with empty text is
 * deliberately not treated as empty because the provider can attach fields,
 * drawings or invisible data to a textless inline.
 */
function estLeCorpsInitialVideDansSdm(
  document: unknown,
  proofBody: CanvasEmptyDocumentProof['body'],
): boolean {
  // `inlines: []` only has the qualified "no physical content" meaning in
  // SDM/1. Keep a newer or malformed provider model readable, but never turn
  // it into the one-shot authority that may replace the complete Word body.
  if (!estEnregistrement(document) || document.modelVersion !== 'sdm/1' || !Array.isArray(document.body)) return false;
  if (proofBody.kind === 'zero_blocks') return document.body.length === 0;
  if (document.body.length !== 1) return false;
  const [node] = document.body;
  if (!estEnregistrement(node) || node.kind !== 'paragraph' || !estEnregistrement(node.paragraph)) return false;
  const inlines = node.paragraph.inlines;
  if (!Array.isArray(inlines)) return false;
  return inlines.length === 0;
}

/**
 * `images.list` cannot be used here: its public v2.15 envelope is global and
 * does not expose story scope or an evaluated revision. `doc.get({})` instead
 * returns the complete body SDM. The surrounding snapshot and final `info()`
 * reads fence that body at one revision. Any unavailable or malformed SDM is a
 * normal readable inspection, never a write authorisation.
 */
async function prouveLaStructureInitialeDuCorps(
  doc: SuperDocDocumentApi,
  body: CanvasEmptyDocumentProof['body'],
  verifierEpoque: (etape: string) => void,
): Promise<boolean> {
  if (typeof doc.get !== 'function') return false;
  try {
    const document = await doc.get({});
    verifierEpoque('inspect:apres-preuve-structure-document-vide');
    return estLeCorpsInitialVideDansSdm(document, body);
  } catch {
    return false;
  }
}

/**
 * Uses two narrow, explicitly unfiltered SuperDoc discoveries only for a body
 * which is already a candidate for first-write initialisation. The ordinary
 * snapshot keeps its wider, model-facing reads intact; their best-effort zero
 * counts are intentionally not evidence of absence.
 */
async function lirePreuveDeDocumentVide(
  doc: SuperDocDocumentApi,
  revision: string,
  body: CanvasEmptyDocumentProof['body'],
  verifierEpoque: (etape: string) => void,
): Promise<CanvasEmptyDocumentProof | undefined> {
  if (typeof doc.trackChanges?.list !== 'function' || typeof doc.comments?.list !== 'function') return undefined;

  let reponseRevisions: unknown;
  let reponseCommentaires: unknown;
  let structureInitialeDuCorps: boolean;
  try {
    [reponseRevisions, reponseCommentaires, structureInitialeDuCorps] = await Promise.all([
      doc.trackChanges.list({ in: 'all', limit: 1, offset: 0 }),
      doc.comments.list({ includeResolved: true, includeThreadReplies: true, limit: 1, offset: 0 }),
      prouveLaStructureInitialeDuCorps(doc, body, verifierEpoque),
    ]);
  } catch {
    return undefined;
  }
  verifierEpoque('inspect:apres-preuve-document-vide');
  if (
    !structureInitialeDuCorps
    || !prouveZeroDeDecouverte(reponseRevisions, revision)
    || !prouveZeroDeDecouverte(reponseCommentaires, revision)
  ) {
    return undefined;
  }
  return {
    revision,
    body,
    trackedChanges: { scope: 'all', total: 0 },
    comments: { includeResolved: true, includeThreadReplies: true, total: 0 },
  };
}

function rangesPublicsDansLeContenu(
  projection: CanvasMarkdownProjection,
  returnedEnd: number,
): Pick<CanvasInspectionRead, 'publicRanges' | 'publicRangesTotal' | 'publicRangesComplete' | 'textListPrefixes'> {
  const publics = projection.blocks.filter((block): block is Extract<CanvasProjectionBlock, { identity: 'public' }> => (
    block.identity === 'public' && block.outputRange.end <= returnedEnd
  ));
  const publicRanges = publics.slice(0, CANVAS_INSPECT_PUBLIC_RANGE_LIMIT_MAX).map((block) => ({
    nodeId: block.nodeId,
    nodeType: block.nodeType,
    start: block.outputRange.start,
    end: block.outputRange.end,
  }));
  // La lecture globale remplace les blocs par le document entier : porter le même contexte ici,
  // à côté des coordonnées, évite d’altérer le texte que le modèle lit et peut réécrire.
  const textListPrefixes = publics.slice(0, CANVAS_INSPECT_PUBLIC_RANGE_LIMIT_MAX).flatMap((block) => {
    const prefix = textListPrefixFromProjection(block);
    return prefix === undefined ? [] : [{ nodeId: block.nodeId, ...prefix }];
  });
  return {
    publicRanges,
    ...(textListPrefixes.length === 0 ? {} : { textListPrefixes }),
    publicRangesTotal: publics.length,
    publicRangesComplete: publics.length === publicRanges.length,
  };
}

/**
 * The historical strict `data.blocks` receipt cannot grow during a front-first
 * rollout: an older backend revalidates it strictly. Long-block continuation
 * metadata therefore travels in the additive outer result, while the bounded
 * block text remains exactly the pre-#1606 shape.
 */
function continuationsTexteDesBlocs(
  blocks: readonly CanvasProjectionBlock[],
): CanvasInspectionTextContinuation[] {
  return blocks.flatMap((block) => {
    if (block.identity !== 'public' || block.textTruncated !== true) return [];
    const textRange = { start: 0, end: block.text.length };
    return [{
      nodeId: block.nodeId,
      textLength: block.fullText.length,
      textRange,
      textWindow: {
        offset: textRange.end,
        limit: Math.min(CANVAS_DOCUMENT_READ_CONTENT_MAX_LENGTH, block.fullText.length - textRange.end),
      },
    }];
  });
}

function lectureDeDocument(
  projection: CanvasMarkdownProjection,
  snapshot: CanvasSnapshot,
): CanvasInspectionRead {
  const totalCharacters = projection.content.length;
  let returnedEnd = totalCharacters;
  if (totalCharacters > CANVAS_DOCUMENT_READ_CONTENT_MAX_LENGTH) {
    const endsAtOrBeforeBudget = projection.blocks
      .map((block) => block.outputRange.end)
      .filter((end) => end <= CANVAS_DOCUMENT_READ_CONTENT_MAX_LENGTH);
    returnedEnd = endsAtOrBeforeBudget.length === 0
      ? CANVAS_DOCUMENT_READ_CONTENT_MAX_LENGTH
      : Math.max(...endsAtOrBeforeBudget);
  }

  const isComplete = returnedEnd === totalCharacters;
  const nextBlock = isComplete
    ? undefined
    : projection.blocks.find((block) => block.outputRange.end > returnedEnd);
  let continuation: CanvasInspectionRead['continuation'];
  if (!isComplete) {
    if (nextBlock !== undefined && nextBlock.outputRange.start < returnedEnd) {
      if (nextBlock.identity !== 'public') {
        throw new CanvasProjectionReadError(
          'projection_malformed',
          'canvas_inspect reached its document-read budget inside a projection block without a public identity. '
          + 'It cannot offer a truthful text-window continuation; inspect a smaller stable target after SuperDoc returns a public block identity.',
        );
      }
      continuation = {
        kind: 'textWindow',
        nodeId: nextBlock.nodeId,
        textWindow: {
          offset: returnedEnd - nextBlock.outputRange.start,
          limit: Math.min(CANVAS_DOCUMENT_READ_CONTENT_MAX_LENGTH, nextBlock.fullText.length - (returnedEnd - nextBlock.outputRange.start)),
        },
      };
    } else {
      continuation = {
        kind: 'blockWindow',
        blockOffset: projection.blocks.filter((block) => block.outputRange.end <= returnedEnd).length,
        blockLimit: CANVAS_INSPECT_BLOCK_LIMIT_DEFAULT,
      };
    }
  }

  return {
    scope: 'document',
    content: projection.content.slice(0, returnedEnd),
    completeness: {
      status: isComplete ? 'complete' : 'incomplete',
      totalCharacters,
      returnedRange: { start: 0, end: returnedEnd },
      // `blocks.list()` is deliberately bounded to 250 for a global read. If
      // SuperDoc did not publish a total, only its complete native projection
      // can truthfully replace that lower bound — it is also the map used to
      // calculate the following block-window continuation.
      totalBlocks: projection.hasCompleteTopLevelBlockMap ? projection.blocks.length : snapshot.counts.blocks,
      totalBlocksExact: projection.hasCompleteTopLevelBlockMap || snapshot.counts.blocksTotalExact,
    },
    ...rangesPublicsDansLeContenu(projection, returnedEnd),
    ...(continuation === undefined ? {} : { continuation }),
  };
}

function lectureDeContinuationTexte(
  apparies: readonly CanvasProjectionBlock[] | undefined,
  input: NonNullable<CanvasConversationInspectEnvelope['input']['textWindow']>,
): CanvasInspectionRead {
  const block = apparies?.[0];
  if (block === undefined || block.identity !== 'public') {
    throw new CanvasProjectionReadError(
      'projection_malformed',
      'canvas_inspect could not resolve the requested textWindow to exactly one public projection block. '
      + 'Refresh the final target by nodeId before reading a long-block continuation.',
    );
  }
  const start = Math.min(input.offset, block.fullText.length);
  const end = Math.min(block.fullText.length, start + input.limit);
  const complete = end === block.fullText.length;
  return {
    scope: 'textWindow',
    content: block.fullText.slice(start, end),
    completeness: {
      status: complete ? 'complete' : 'incomplete',
      totalCharacters: block.fullText.length,
      returnedRange: { start, end },
      totalBlocks: 1,
      totalBlocksExact: true,
    },
    publicRanges: [],
    publicRangesTotal: 0,
    publicRangesComplete: true,
    ...(complete
      ? {}
      : {
        continuation: {
          kind: 'textWindow' as const,
          nodeId: block.nodeId,
          textWindow: {
            offset: end,
            limit: Math.min(CANVAS_DOCUMENT_READ_CONTENT_MAX_LENGTH, block.fullText.length - end),
          },
        },
      }),
  };
}

/**
 * Les images listées par l'éditeur, ou `undefined` quand la lecture lève.
 *
 * Une liste d'images en échec ne doit pas priver l'agent de la lecture du
 * document : les images restent alors `[image]`, comme avant ce relevé.
 */
async function lireLesImagesOuRien(doc: SuperDocDocumentApi): Promise<ImageListee[] | undefined> {
  try {
    return await listerLesImages(doc);
  } catch {
    return undefined;
  }
}

/**
 * Reads all material used by an inspect receipt under one revision fence.
 *
 * `projectMarkdown` is deliberately paired with the ordinary snapshot instead
 * of replacing it: the former is the only source of the visible review view
 * and its addresses, while the latter remains the source of open revision and
 * comment counts. The last `doc.info()` makes the pairing fail closed when the
 * lawyer edits while either read is in flight.
 */
async function lireInspectionProjeteeStable(
  doc: SuperDocDocumentApi,
  input: CanvasConversationInspectEnvelope['input'],
  verifierEpoque: (etape: string) => void,
): Promise<InspectionProjeteeStable> {
  const mode: CanvasInspectionProjection = input.projection ?? 'final';
  let derniereIncoherence = 'the projection did not report the snapshot revision';

  for (const delai of PROJECTION_FENCE_RETRY_DELAYS_MS) {
    await attendre(delai);
    verifierEpoque('inspect:avant-snapshot-projete');
    const snapshot = await readCanvasSnapshot(doc, fenetreDuSnapshot(input));
    verifierEpoque('inspect:apres-snapshot-projete');
    const projection = nommerLesImagesDeLaProjection(await lireProjectionMarkdown(doc, mode), await lireLesImagesOuRien(doc));
    verifierEpoque('inspect:apres-projection-native');
    const apparies = input.selector === undefined
      ? undefined
      : await blocsProjectionQuiApparientSelecteur(doc, projection, input.selector);
    verifierEpoque('inspect:apres-resolution-selecteur-projete');
    const corpsVideInitial = formeDeCorpsVideInitial(input, snapshot, projection);
    const emptyDocumentProof = corpsVideInitial === undefined
      ? undefined
      : await lirePreuveDeDocumentVide(doc, snapshot.revision, corpsVideInitial, verifierEpoque);
    const revisionFinale = (await lireLaRevisionDuDocument(doc)) ?? REVISION_ABSENTE;
    if (snapshot.revision === projection.projection.evaluatedRevision && revisionFinale === snapshot.revision) {
      return {
        snapshot,
        projection,
        ...(apparies === undefined ? {} : { apparies }),
        ...(emptyDocumentProof === undefined ? {} : { emptyDocumentProof }),
      };
    }
    derniereIncoherence = `snapshot=${snapshot.revision}, projection=${projection.projection.evaluatedRevision}, current=${revisionFinale}`;
  }

  throw new CanvasProjectionReadError(
    'projection_revision_stale',
    `canvas_inspect could not obtain one stable projection after ${PROJECTION_FENCE_RETRY_DELAYS_MS.length} attempts: ${derniereIncoherence}.`,
  );
}

/**
 * Nombre de tentatives pour lire la forme à UNE révision. L'avocat peut taper
 * pendant la lecture : chaque tentative reprend la lecture projetée, puis
 * l'export. Trois passes couvrent une frappe isolée ; au-delà le document
 * bouge en continu et le refus le dit, plutôt que de rendre une forme datée.
 */
const TENTATIVES_DE_LECTURE_DE_FORME = 3;

/**
 * La forme n'a pas pu être rattachée à la révision du reçu. `capability` nomme
 * la surface de l'éditeur qui a fait défaut ; le message, en anglais, part tel
 * quel dans le reçu que lit le modèle.
 */
class FormeNonCloturee extends Error {
  constructor(
    readonly code: 'format_revision_unavailable' | 'format_revision_unstable',
    readonly capability: 'editor.doc.info' | 'editor.doc.export.toDocx',
    message: string,
  ) {
    super(message);
    this.name = 'FormeNonCloturee';
  }
}

/**
 * Ce qu'un échec de lecture de forme rend à l'agent, ou `null` si l'erreur
 * n'en est pas un. UN seul chemin pour les trois causes — export refusé,
 * fichier illisible, forme non clôturée : chacune porte déjà sa cause et son
 * issue, et la rendre sous le message d'une autre lecture (« safe projection »,
 * « re-open the canvas ») enverrait l'agent réparer ce qui n'a rien.
 */
function refusDeLectureDeForme(error: unknown): { message: string; code: string; capability: string } | null {
  if (error instanceof ExportDeLActeImpossible) return { message: error.message, code: error.code, capability: 'editor.doc.export.toDocx' };
  if (error instanceof LectureDeFormeImpossible) return { message: error.message, code: 'format_read_failed', capability: 'canvas.inspect.formatReader' };
  if (error instanceof FormeNonCloturee) return { message: error.message, code: error.code, capability: error.capability };
  if (error instanceof LieuDeFormeAbsent) return { message: error.message, code: 'format_place_not_found', capability: 'canvas.inspect.formatReader' };
  if (error instanceof RangHorsDuDocument) {
    return {
      message: error.totalParagraphes === 0
        ? `start_paragraph=${error.depuis} is not a paragraph of the open document: it has no non-empty paragraph. Nothing was modified. There is no form to read until the document has text.`
        : `start_paragraph=${error.depuis} is not a paragraph of the open document, which has ${error.totalParagraphes} non-empty paragraph(s). Nothing was modified. `
          + `Run canvas_inspect again with formatReading.start_paragraph between 1 and ${error.totalParagraphes}.`,
      code: 'format_reading_start_out_of_range',
      capability: 'canvas.inspect.formatReader',
    };
  }
  return null;
}

/**
 * Ce qu'une lecture avec forme arme pour les écritures de texte : parmi les
 * identifiants que sa page montre sur un paragraphe du corps, ceux que la
 * projection finale de l'éditeur publie à la même révision.
 *
 * WHY cette confrontation : le courtier n'arme une écriture de contenu que sur
 * une identité que l'éditeur déclare publique (`identity:'public'`), jamais sur
 * un identifiant tiré d'un texte. Les identifiants de la page viennent du
 * fichier exporté ; la projection est celle de la même inspection, clôturée sur
 * la même révision (`lireInspectionEtForme`). Un identifiant que l'éditeur ne
 * publie pas reste lisible et n'est pas armé — c'est aussi ce qui écarte un
 * paragraphe de cellule, que la projection ne publie pas (elle publie le
 * tableau) et que `rewrite_block` refuse.
 */
function porteeDeLaLectureAvecForme(
  projection: CanvasMarkdownProjection,
  revision: string,
  identifiantsDuCorps: readonly string[],
): CanvasFormatReadingScope {
  const publics = new Set(projection.blocks.flatMap((block) => (block.identity === 'public' ? [block.nodeId] : [])));
  return { revision, nodeIds: identifiantsDuCorps.filter((nodeId) => publics.has(nodeId)) };
}

/**
 * La lecture d'inspection, plus la forme quand `formatOf` ou `formatReading`
 * la demande (le contrat d'entrée refuse les deux ensemble).
 *
 * UN export par tentative, donc par appel d'outil dans le cas nominal — jamais
 * un par paragraphe. La forme est rattachée à la révision du reçu par la même
 * clôture que la projection : si le document a changé entre la lecture et
 * l'export, la forme lue ne décrit plus la révision annoncée, et l'agent
 * écrirait ensuite sur une forme qu'il n'a pas lue.
 */
async function lireInspectionEtForme(
  doc: SuperDocDocumentApi,
  input: CanvasConversationInspectEnvelope['input'],
  verifierEpoque: (etape: string) => void,
): Promise<InspectionProjeteeStable & { format?: CanvasFormatRead; lectureAvecForme?: LectureAvecFormeDeLActe }> {
  const { formatOf, formatReading } = input;
  const lireDansLExport = formatOf !== undefined
    ? async (octets: Uint8Array) => ({ format: formePourLAgent(await lireLaForme(octets), formatOf) })
    : formatReading !== undefined
      ? async (octets: Uint8Array) => ({ lectureAvecForme: await lectureAvecFormePourLAgent(octets, formatReading) })
      : undefined;
  if (lireDansLExport === undefined) return lireInspectionProjeteeStable(doc, input, verifierEpoque);

  for (let tentative = 0; tentative < TENTATIVES_DE_LECTURE_DE_FORME; tentative += 1) {
    const lecture = await lireInspectionProjeteeStable(doc, input, verifierEpoque);
    // Sans révision publiée, « la révision n'a pas bougé » comparerait la
    // sentinelle d'absence à elle-même : la clôture passerait sans rien clore.
    if (lecture.snapshot.revision === REVISION_ABSENTE) {
      throw new FormeNonCloturee(
        'format_revision_unavailable',
        'editor.doc.info',
        'the editor does not publish a document revision (editor.doc.info), so the form cannot be tied to the document state it describes. '
        + 'Nothing was modified. Form reads are unavailable on this document; other canvas_inspect reads still work.',
      );
    }
    verifierEpoque('inspect:avant-export-en-memoire');
    const octets = await exporterLActeEnMemoire(doc);
    verifierEpoque('inspect:apres-export-en-memoire');
    if ((await lireLaRevisionDuDocument(doc)) === lecture.snapshot.revision) {
      return { ...lecture, ...(await lireDansLExport(octets)) };
    }
  }
  throw new FormeNonCloturee(
    'format_revision_unstable',
    'editor.doc.export.toDocx',
    `the document kept changing while its Word file was exported (${TENTATIVES_DE_LECTURE_DE_FORME} attempts): the lawyer is typing, or a write is in flight. `
    + `Nothing was modified. Run the same canvas_inspect with ${formatOf !== undefined ? 'formatOf' : 'formatReading'} again.`,
  );
}

async function executeInspect(
  instance: SuperDocInstance | null,
  doc: SuperDocDocumentApi,
  request: CanvasConversationInspectEnvelope,
  garde: GardeDEpoque,
  context?: CanvasExecutionContext,
): Promise<CanvasSuperDocResultPayload> {
  if (typeof doc.info !== 'function' || typeof doc.blocks?.list !== 'function' || typeof doc.projectMarkdown !== 'function') {
    return receiptError(
      request,
      'canvas_inspect requires editor.doc.info, editor.doc.blocks.list, and editor.doc.projectMarkdown before the current document can be inspected safely.',
      { code: 'capability_unsupported', capability: 'editor.doc.info/editor.doc.blocks.list/editor.doc.projectMarkdown' },
    );
  }

  const verifierEpoque = creerVerificateurDEpoque(
    garde.attendue,
    garde.lireCourante,
    { tentee: false },
  );
  try {
    // Lue AVANT le snapshot pour être publiée avec lui : le modèle apprend dans
    // la même trame ce que contient le document et ce qu'il peut y faire. La
    // borne de qualification laisse l'inspection continuer sur un fournisseur
    // muet — seul l'accès aux mutations devient alors indisponible.
    verifierEpoque('inspect:avant-capacites');
    const capabilityQualification = await qualifierLesCapacitesDuDocument(doc);
    verifierEpoque('inspect:apres-capacites');

    const { snapshot, projection, apparies, emptyDocumentProof, format, lectureAvecForme } = await lireInspectionEtForme(doc, request.input, verifierEpoque);
    const formatReading = lectureAvecForme?.lecture;
    const lectureDocument = estLectureDeDocument(request.input);
    const textSearchMiss = rechercheSansBlocCommun(projection.blocks, request.input.selector, apparies);
    const lectureTexte = request.input.textWindow;
    const fenetre = fenetreDeBlocs(request.input);
    // Un sélecteur rend TOUS ses appariements, plus leur nombre total — c'est
    // ce qui manquait le plus à l'agent : rendre le premier seul lui cacherait
    // qu'il y a une ambiguïté à lever avant d'agir de façon destructive.
    // Une lecture de forme ne publie aucun bloc : pour le broker elle garde la
    // portée acquise à la même révision, comme une recherche sans résultat (et,
    // comme toute lecture, révoque les autorisations à usage unique). La lecture
    // avec forme y AJOUTE les paragraphes qu'elle a rendus (`formatReadingScope`).
    const blocs = lectureDocument || lectureTexte !== undefined || format !== undefined || formatReading !== undefined
      ? []
      : apparies === undefined
      ? projection.blocks.slice(fenetre.offset, fenetre.offset + fenetre.limit)
      : apparies.slice(fenetre.offset, fenetre.offset + fenetre.limit);
    const inspectionRead = lectureDocument
      ? lectureDeDocument(projection, snapshot)
      : lectureTexte === undefined
        ? undefined
        : lectureDeContinuationTexte(apparies, lectureTexte);
    const inspectionTextContinuations = continuationsTexteDesBlocs(blocs);
    // L'identité du receipt reste celle de l'enveloppe routée. La garde amont
    // refuse le mismatch avant le Document API, et les contrôles ci-dessus
    // empêchent un changement de montage pendant les lectures.
    //
    // ⚠️ La clé du jeton passe par `cleDeMontage` (#1336) et n'est JAMAIS
    // recomposée à la main : le courtier backend recompose le même jeton avec
    // cette fonction après une mutation (`CanvasSessionBroker`), et deux
    // compositions divergeraient le jour où la forme change.
    const cleDuMontage = cleDeMontage({
      forme: 'conversation',
      conversationId: request.conversationId,
      mountEpoch: request.mountEpoch,
    });
    // Le mode décrit la surface actuellement montée. Il n'est ni une
    // qualification de capacité, ni une preuve de persistance : les trois
    // signaux sont volontairement publiés et consommés séparément.
    const documentMode = lireLeModeDuDocument(instance);
    verifierEpoque('inspect:emission');
    return receiptOk(request, {
      requestId: request.requestId,
      conversationId: request.conversationId,
      mountEpoch: request.mountEpoch,
      snapshotToken: `${cleDuMontage}:${snapshot.revision}:${request.requestId}`,
      revision: snapshot.revision,
      projection: projection.projection,
      documentMode,
      ...(context?.document ? { document: context.document } : {}),
      counts: snapshot.counts,
      blocks: toModelSafeCanvasProjectionBlocks(blocs),
      // Publié seulement quand un sélecteur a été demandé : il dit combien de
      // blocs du document ENTIER apparient, et combien sont dans `blocks`.
      // Sans lui, `blocks: []` serait indiscernable d'un document vide, et
      // `blocks.length === 3` d'un appariement exhaustif alors que la fenêtre
      // en a coupé quarante.
      ...(apparies === undefined
        ? {}
        : {
          selectorResolution: {
            matchCount: apparies.length,
            returnedCount: blocs.length,
            blockOffset: fenetre.offset,
            blockLimit: fenetre.limit,
          },
        }),
      // ⚠️ LE domaine que ce ticket ouvre (#994). Les révisions étaient lues à
      // chaque inspection et RÉDUITES à leur nombre : le modèle savait qu'il y
      // avait trois modifications à relire, et n'avait aucun moyen d'en nommer
      // une seule — donc `accept_tracked_changes` et `reject_tracked_changes`
      // étaient inatteignables, sauf à inventer un identifiant, ce que l'exemple
      // du catalogue lui suggérait justement de faire.
      //
      // Publié SUR DEMANDE : la lecture, elle, a lieu de toute façon (elle
      // alimente `counts`), donc le domaine ne coûte aucun appel au fournisseur
      // — seulement des jetons. Sur un acte relu par un confrère la liste peut
      // être longue, et la servir à chaque inspection mangerait la fenêtre du
      // modèle pour une donnée utile au seul moment de la revue.
      ...(request.input.includeDomains.includes('trackedChanges')
        ? { trackedChanges: snapshot.trackedChanges }
        : {}),
      // ⚠️ MÊME réparation que ci-dessus, quinze jours plus tard, sur les
      // commentaires (#996). `resolve_comments` et `reply_to_comment` exigent
      // tous deux un `commentId` et l'inspection ne publiait qu'un
      // `counts.comments` — un NOMBRE : les deux verbes étaient **inappelables**.
      //
      // Même contrat d'absence, à ne pas confondre : le champ absent dit « le
      // domaine n'a pas été demandé », jamais « il n'y a aucun commentaire ». Le
      // seul énoncé d'absence est `counts.comments === 0`.
      ...(request.input.includeDomains.includes('comments')
        ? { comments: snapshot.comments }
        : {}),
      // ⚠️ TROISIÈME occurrence de la même réparation, après les révisions
      // (#994) et les commentaires (#996) : `edit_footnote` et
      // `delete_footnote` exigent tous deux un `note_id`, et rien ne le
      // publiait — les deux verbes seraient **inappelables** dès leur mise en
      // service, sauf à inventer un identifiant.
      //
      // ⚠️ Contrairement à ses deux aînés, ce domaine n'a AUCUN compte pour
      // l'annoncer : `counts` est `.strict()` au contrat partagé et ne déclare
      // pas `footnotes`. Le modèle ne peut donc pas déduire qu'il y a des notes
      // à demander — il doit demander le domaine pour le savoir, d'où la ligne
      // qui le décrit dans `INSPECT_DESCRIPTION` côté backend.
      //
      // ⚠️ Et ce que cette liste dit n'est PAS ce que l'avocat verra : sous
      // régime suivi, une note supprimée y reste jusqu'à l'acceptation de la
      // révision (mesuré 2026-08-26, §9.4 de `superdoc-document-api.md`).
      ...(request.input.includeDomains.includes('footnotes')
        ? { footnotes: snapshot.footnotes }
        : {}),
      // ⚠️ QUATRIÈME occurrence de la même réparation, après les révisions
      // (#994), les commentaires (#996) et les notes : `set_header_footer`
      // écrit dans une zone dont l'agent ne pouvait RIEN savoir — ni si elle
      // porte déjà la ligne du cabinet, ni si le papier à en-tête y est, ni si
      // la première page est distincte. Il n'avait donc que deux conduites, et
      // les deux sont mauvaises : écraser à l'aveugle, ou ne rien proposer.
      //
      // ⚠️ Et une différence de NATURE avec les trois autres : ce domaine est
      // LU ICI, sur demande, jamais dans `readCanvasSnapshot`. Les trois aînés
      // sont relus de toute façon pour alimenter `counts` ; celui-ci coûte
      // jusqu'à dix-huit appels fournisseur par section, et les dépenser à
      // chaque inspection ralentirait tout le reste de l'agent.
      ...(request.input.includeDomains.includes('headersFooters')
        ? { headersFooters: await lireLesEnTetes(doc) }
        : {}),
      // La forme des paragraphes visés par `formatOf` (#1859), lue dans le
      // fichier exporté en mémoire — la seule lecture qui voit ce qu'un style
      // nommé apporte. Même contrat d'absence que les domaines ci-dessus : le
      // champ absent dit « aucune lecture de forme demandée ».
      ...(format === undefined ? {} : { format }),
      // La lecture avec forme de l'acte (`formatReading`), du même export : même contrat d'absence.
      ...(formatReading === undefined ? {} : { formatReading }),
      // Pont compatible ancien backend : `data` est revalidé par un schema
      // strict encore déployé. La qualification détaillée est donc au niveau
      // extérieur du résultat, où un backend antérieur l'ignore sans perdre
      // l'inspection. Une lecture globale est rendue non-mutante par le broker,
      // jamais en déduisant cette propriété d'un tableau vide.
      executableActions: capabilityQualification.status === 'qualified'
        ? capabilityQualification.executableActions
        : [],
    }, {
      capabilityQualification,
      ...(emptyDocumentProof === undefined ? {} : { emptyDocumentProof }),
      ...(inspectionRead === undefined ? {} : { inspectionRead }),
      ...(inspectionTextContinuations.length === 0 ? {} : { inspectionTextContinuations }),
      ...(lectureAvecForme === undefined
        ? {}
        : { formatReadingScope: porteeDeLaLectureAvecForme(projection, snapshot.revision, lectureAvecForme.identifiantsDuCorps) }),
      ...(textSearchMiss === undefined ? {} : { textSearchMiss }),
    });
  } catch (error) {
    if (estEpoqueDepassee(error)) {
      return receiptError(
        request,
        `${error.message} The inspection result was discarded. Run canvas_inspect again: it reads the document mounted now; continue from that read.`,
        { code: 'DOCUMENT_SESSION_CHANGED', capability: 'canvas.inspect.executor' },
      );
    }
    if (isCanvasProjectionReadError(error)) {
      return receiptError(
        request,
        `canvas_inspect could not produce a safe projection for requestId=${request.requestId}. ${error.message}`,
        { code: error.code, capability: 'editor.doc.projectMarkdown' },
      );
    }
    const refusDeForme = refusDeLectureDeForme(error);
    if (refusDeForme !== null) {
      return receiptError(
        request,
        `canvas_inspect ${request.input.formatReading !== undefined ? 'formatReading' : 'formatOf'} failed for requestId=${request.requestId}: ${refusDeForme.message}`,
        { code: refusDeForme.code, capability: refusDeForme.capability },
      );
    }
    const code = isCanvasSnapshotReadError(error) ? error.code : 'document_api_error';
    // La cause du fournisseur part dans le reçu : sans elle, un document que l'éditeur ne sait pas lire
    // et un canvas à rouvrir rendent le même message (signalement du 2026-10-09).
    const cause = (error instanceof Error ? `${error.name}: ${error.message}` : String(error)).slice(0, 240);
    return receiptError(
      request,
      `canvas_inspect could not read the current document snapshot for requestId=${request.requestId} (${cause}); re-open the canvas and inspect again before mutating.`,
      { code, capability: 'editor.doc.blocks.list' },
    );
  }
}

async function executeAction(
  instance: SuperDocInstance | null,
  doc: SuperDocDocumentApi,
  request: CanvasConversationActionEnvelope,
  garde: GardeDEpoque,
  resources: CanvasExecutionResources,
  executionContext?: CanvasExecutionContext,
): Promise<CanvasSuperDocResultPayload> {
  // Cette garde est volontairement redondante avec `canvasAgentRequestGuard` :
  // l'exécuteur est aussi une couture appelable par des tests ou un futur
  // intégrateur. Un frame pré-#1605 lisible ne doit jamais transformer
  // l'absence de portée en permission d'écrire dans le document brut.
  const inspectedPublicNodeIds = request.inspectedPublicNodeIds;
  if (inspectedPublicNodeIds === undefined) {
    const message =
      `canvas_perform_action ${request.action.action} refused before capability lookup and mutation: `
      + 'the action envelope omits inspectedPublicNodeIds, so it cannot prove which blocks were published by a native '
      + 'final projection. Nothing was written. Run canvas_inspect with projection="final" from the current Canvas bundle '
      + 'before retrying this action.';
    return receiptError(request, message, {
      code: 'CANVAS_INSPECTION_REQUIRED',
      data: {
        receipt: abortedBeforeMutation({
          intent: request.action.action,
          snapshot: SNAPSHOT_INDISPONIBLE,
          code: 'CANVAS_INSPECTION_REQUIRED',
          message,
          recovery: {
            action: 'reinspect',
            reason: 'The action has no native final public-identity scope; inspect before acting.',
          },
        }),
      },
    });
  }

  // Aucune action ne bascule le mode de l'éditeur : il est celui du régime de
  // suivi, posé par le scope documentaire. Une surface en lecture seule — ou un
  // mode fournisseur illisible — se refuse donc ICI, avant d'appeler le
  // provider, plutôt que d'être apprise par son refus (#1606).
  const documentMode = lireLeModeDuDocument(instance);
  if (documentMode === 'viewing' || documentMode === 'unknown') {
    // Le sélecteur Editing/Suggesting/Viewing de SuperDoc est masqué dans la
    // barre du canvas : l'avocat n'a aucun geste pour « sortir de la lecture
    // seule ». Le mode peut n'être que transitoire (montage en cours) ; s'il
    // persiste, seul un nouveau montage le rétablit.
    const message =
      `canvas_perform_action ${request.action.action} refused before capability lookup and mutation: `
      + `documentMode=${documentMode}, so Canvas has no authority to start an edit or switch modes. `
      + 'No provider call was made and nothing was written. Run canvas_inspect once: if documentMode is then '
      + 'editing or suggesting, resend this action; if it is still viewing or unknown, ask the lawyer to reload the page.';
    return receiptError(request, message, {
      code: 'CANVAS_DOCUMENT_NOT_WRITABLE',
      data: {
        receipt: abortedBeforeMutation({
          intent: request.action.action,
          snapshot: SNAPSHOT_INDISPONIBLE,
          code: 'CANVAS_DOCUMENT_NOT_WRITABLE',
          message,
          // Le champ machine dit la même chose que le message : une relecture
          // est ici réellement utile, elle relit le mode du document.
          recovery: {
            action: 'reinspect',
            reason: 'Nothing was written. Re-read once to learn the current documentMode; if it is still viewing or unknown, ask the lawyer to reload the page.',
          },
        }),
      },
    });
  }

  // L'unique état partagé entre le vérificateur et l'exécution : une mutation
  // a-t-elle déjà été TENTÉE ? C'est lui qui départage « refusé, rien n'a
  // bougé » de « non vérifiable, l'effet n'est plus attribuable ».
  const etatMutation = { tentee: false };
  const verifierEpoque = creerVerificateurDEpoque(garde.attendue, garde.lireCourante, etatMutation);

  // ⚠️ La capacité se contrôle AVANT toute mutation (#987), et sur ce que le
  // fournisseur DÉCLARE — plus sur un `typeof doc.X === 'function'` deviné
  // verbe par verbe. C'est cette devinette qui a laissé `undo_changes` viser
  // `doc.undo`, une méthode inexistante, pendant toute sa mise en service.
  //
  // Le refus arrive donc avant que l'exécuteur n'ait rien tenté : `etatMutation`
  // reste intact, et le receipt dit « rien n'a bougé » plutôt que « non
  // vérifiable ».
  const capabilityQualification = await qualifierLesCapacitesDuDocument(doc);
  verifierEpoque('action:apres-qualification-capacites');
  if (capabilityQualification.status === 'unqualified') {
    const message = messageDeCapacitesNonQualifiees(request.action.action, capabilityQualification.reason);
    return receiptError(request, message, {
      code: 'CANVAS_MUTATION_CAPABILITIES_UNQUALIFIED',
      capability: capabilityQualification.reason,
      data: {
        receipt: abortedBeforeMutation({
          intent: request.action.action,
          snapshot: SNAPSHOT_INDISPONIBLE,
          code: 'CANVAS_MUTATION_CAPABILITIES_UNQUALIFIED',
          message,
        }),
      },
    });
  }

  // `qualified` ne peut provenir que d'une lecture réussie, mise en cache sur
  // CE document. Cette seconde lecture est donc instantanée ; la branche garde
  // un refus fermé si un futur changement de cache rompait cette invariant.
  const capacites = await lireLesCapacites(doc);
  if (capacites === null) {
    const message = messageDeCapacitesNonQualifiees(request.action.action, 'capabilities_unreadable');
    return receiptError(request, message, {
      code: 'CANVAS_MUTATION_CAPABILITIES_UNQUALIFIED',
      capability: 'capabilities_unreadable',
      data: {
        receipt: abortedBeforeMutation({
          intent: request.action.action,
          snapshot: SNAPSHOT_INDISPONIBLE,
          code: 'CANVAS_MUTATION_CAPABILITIES_UNQUALIFIED',
          message,
        }),
      },
    });
  }
  const manquantes = operationsManquantes(request.action.action, capacites);
  if (manquantes.length > 0) {
    return receiptError(
      request,
      messageDeCapaciteAbsente(request.action.action, manquantes),
      {
        code: 'CAPABILITY_UNAVAILABLE',
        capability: manquantes.join(', '),
        data: {
          receipt: abortedBeforeMutation({
            intent: request.action.action,
            snapshot: SNAPSHOT_INDISPONIBLE,
            code: 'CAPABILITY_UNAVAILABLE',
            message: messageDeCapaciteAbsente(request.action.action, manquantes),
          }),
        },
      },
    );
  }
  // Plan-based verbs (`apply_edit_plan`, `format_text`, `format_paragraphs`) have a second
  // provider-owned capability surface: `planEngine.supportedStepOps`. It stays
  // outside flat `operations`, so a forged envelope must be rejected here as
  // well as hidden by the dynamic model schema returned after canvas_inspect.
  const etapesPlanManquantes = etapesPlanNativesManquantes(request.action.action, capacites, doc);
  if (etapesPlanManquantes.length > 0) {
    const message = `${request.action.action} is unavailable on this mounted document because its native plan engine does not expose `
      + `${etapesPlanManquantes.join(', ')}. Nothing was modified; `
      + (request.action.action === 'apply_edit_plan'
        ? 'send these edits one by one with replace_text, if canvasAgentState reports it as allowed.'
        : 'use an action canvasAgentState reports as allowed, or tell the lawyer this cannot be done on this document.');
    return receiptError(request, message, {
      code: 'CAPABILITY_UNAVAILABLE',
      capability: etapesPlanManquantes.join(', '),
      data: {
        receipt: abortedBeforeMutation({
          intent: request.action.action,
          snapshot: SNAPSHOT_INDISPONIBLE,
          code: 'CAPABILITY_UNAVAILABLE',
          message,
        }),
      },
    });
  }

  // ⚠️ LU UNE SEULE FOIS, ICI (#990). Le régime de révision est une propriété du
  // DOCUMENT, réglée par l'avocat. Le relire entre deux opérations d'un même lot
  // laisserait une bascule couper l'action en vol : la moitié des modifications
  // suivie, l'autre non, sur le même geste — c'est le « mi-rouge mi-noir » que
  // ce ticket ferme.
  const regimeSuivi = resources.reviewEnabled;
  const pointDEcriture = resources.writer;
  const resolveurDEnTete = resources.namedLetterheadResolver;

  // ⚠️ Refus NOMMÉ quand le régime du document est « suivi » et que l'opération
  // d'écriture du verbe s'annonce non suivable (#990). Il arrive avant toute
  // mutation, donc le document reste intact et le receipt le dit (`aborted`).
  //
  // Ce contrôle ne PROMET rien : `tracked:true` ne garantit pas qu'une révision
  // sera produite — mesuré le 2026-08-16, `insert` s'annonce suivable et ne
  // produit rien. Il ferme seulement le cas où le fournisseur annonce lui-même
  // qu'il ne sait pas faire ce que l'avocat a demandé. Le reste se tranche à la
  // relecture, dans le receipt (`TRACKING_NOT_APPLIED`).
  const nonSuivables = regimeSuivi ? operationsNonSuivables(request.action.action, capacites) : [];
  if (nonSuivables.length > 0) {
    const message = messageDeSuiviImpossible(request.action.action, nonSuivables);
    return receiptError(request, message, {
      code: 'TRACKING_UNSUPPORTED',
      capability: nonSuivables.join(', '),
      data: {
        receipt: abortedBeforeMutation({
          intent: request.action.action,
          snapshot: SNAPSHOT_INDISPONIBLE,
          code: 'TRACKING_UNSUPPORTED',
          message,
        }),
      },
    });
  }

  try {
    // This ledger is tied to the provider instance AND mount epoch, not to the
    // conversation alone. A remount/reload intentionally loses automatic
    // withdrawal eligibility rather than guessing provenance from Word's
    // author field (which SuperDoc v2 assigns to the connected lawyer).
    const agentTrackedInsertionLedger = instance === null
      ? undefined
      : ledgerForAgentTrackedInsertions(instance, {
        conversationId: request.conversationId,
        mountEpoch: request.mountEpoch,
      });
    // UN réessai par action : il porte le budget d'attente de l'action entière
    // et son bilan, relu après l'exécuteur pour nommer la cause au reçu.
    const reessai = creerReessaiSurRefusPassager({
      // Relu à chaque refus, sur l'instance : c'est le mode qui sépare l'export
      // en cours (document `editing`) d'une lecture seule durable (`viewing`).
      lireLeMode: () => lireLeModeDuDocument(instance),
      // L'attente est un point de suspension : l'avocat a pu ouvrir un autre
      // document. Même garde que `muterSousFences` avant le premier essai.
      avantReprise: () => verifierEpoque('mutation:reprise-apres-refus-passager'),
    });
    const context: CanvasActionContext = {
      doc: doc as CanvasActionContext['doc'],
      regimeSuivi,
      peintre: resources.peintre,
      // La révision vient de la TRAME de l'agent, donc de l'extérieur du typage.
      // Le marquage est légitime ici : l'agent ne peut la connaître que par
      // `canvas_inspect`, dont le producteur publie `snapshot.revision`,
      // c'est-à-dire `doc.info()` — le même espace que le verrou (#980). Une
      // valeur d'un autre espace serait refusée par le provider, pas silencieuse.
      expectedRevision: marquerRevisionDeLAgent(request.expectedRevision),
      // Cette liste est forgée côté broker à partir des seules identités que la
      // projection native finale a rendues publiques. L'absence a déjà été
      // refusée avant toute lecture fournisseur ; [] interdit toute cible
      // brute, plutôt que d'en inventer une depuis le texte projeté.
      inspectedPublicNodeIds: new Set(inspectedPublicNodeIds),
      // La corrélation reste technique : elle relie les timings browser à la
      // trame WS, sans faire remonter le markdown ou les sélecteurs.
      canvasRequestId: request.requestId,
      // Les octets d'image joints par le backend à côté de l'action (#2141) : l'exécuteur les lit ici, jamais dans l'action.
      ...(request.imageBytes === undefined ? {} : { imageJointe: request.imageBytes }),
      ...(executionContext?.onUnitCompleted === undefined ? {} : { onChunkCompleted: executionContext.onUnitCompleted }),
      ...(request.initialBlankTemplateParagraphId === undefined
        ? {}
        : { initialBlankTemplateParagraphId: request.initialBlankTemplateParagraphId }),
      ...(agentTrackedInsertionLedger === undefined ? {} : { agentTrackedInsertionLedger }),
      verifierEpoque,
      // Un appel mutant ne touche PAS au mode de l'éditeur (#1838). Le régime de
      // suivi choisi par l'avocat tient l'éditeur en `suggesting` ou en `editing`
      // (`CanvasDocumentScope`), et le `changeMode` de chaque appel est dérivé du
      // même régime (`modeDeRevision`) : les deux disent la même chose sans
      // bascule. Une bascule autour de l'appel écraserait un clic de l'avocat
      // sur son bouton fait pendant l'écriture.
      mutate: (operation) => {
        etatMutation.tentee = true;
        return reessai.executer(operation);
      },
      // LE POINT D'ÉCRITURE, celui de la génération d'acte (#993). Il est posé
      // dans le scope par `RedactionCanvas` au montage — c'est le seul pont
      // entre le monde hors-React et la file d'écriture, et c'est déjà par lui
      // que la génération orchestrateur passe (#938).
      //
      // Lu ICI plutôt qu'importé par l'exécuteur, pour la même raison que
      // `regimeSuivi` : un exécuteur qui irait chercher le scope lui-même
      // deviendrait intestable sans monter tout le canvas, et rien ne dirait
      // plus, à la lecture de son contexte, de quoi il dépend.
      //
      // ⚠️ `?? undefined` : le scope rend `null` quand aucun writer n'est
      // monté. Le contexte, lui, ne connaît que « présent » ou « absent » — et
      // l'absence y dit « je n'ai pas de point d'écriture », jamais « écrire est
      // impossible ».
      ...(pointDEcriture === null ? {} : { ecrireDocument: pointDEcriture }),
      ...(resolveurDEnTete === null ? {} : { resoudreLEnTeteNomme: resolveurDEnTete }),
      // Le régime se règle sur le scope (la source du bouton), le mode se relit
      // sur l'instance : le reçu de `set_review_mode` dit ce que l'éditeur porte.
      reglerLeSuivi: (suiviActif) => {
        resources.setReviewEnabled(suiviActif);
        return lireLeModeDuDocument(instance);
      },
      // Ce que l'avocat VOIT de ce document, pour le seul verbe qui peut l'effacer
      // sans trace (#1586). Le montage est celui que la garde a autorisé : le
      // refus le nomme, et un lecteur hors contexte sait de quel document il parle.
      visibiliteDuDocument: { montage: garde.attendue, hoteParque: resources.isHostParked },
    };
    const recuDeLExecuteur = await CANVAS_ACTION_EXECUTORS[request.action.action](context, request.action as never) as CanvasActionReceipt;
    const bilanDesRefus = reessai.bilan();
    if (bilanDesRefus.issue !== null) {
      // Seule trace du refus quand il a été surmonté : l'agent n'en voit rien.
      captureCanvasAgentTransportDiagnostic({
        stage: 'transient_refusal_settled',
        requestId: request.requestId,
        tool: request.tool,
        action: request.action.action,
        mountEpoch: request.mountEpoch,
        outcome: bilanDesRefus.issue,
        refusalCount: bilanDesRefus.refus,
        waitedMs: bilanDesRefus.attenteMs,
      });
    }
    const receipt = nommerLEnregistrementEnCours(recuDeLExecuteur, bilanDesRefus);
    if (agentTrackedInsertionLedger !== undefined) {
      if (receipt.status === 'ok') {
        agentTrackedInsertionLedger.advanceAfterAgentAction({
          preRevision: receipt.preSnapshot.revision,
          postRevision: receipt.postSnapshot.revision,
        });
      } else if (receipt.status !== 'aborted') {
        // A partial, failed, or non-verifiable action no longer gives a
        // complete causal account of document state. Keep no auto-review
        // authority after it; the next action must use native behavior.
        agentTrackedInsertionLedger.invalidate();
      }
    }
    // Dernière ré-assertion AVANT l'émission : un receipt qui décrirait un
    // document qui n'est plus monté ferait agir l'agent sur le mauvais.
    verifierEpoque('receipt:emission');
    // Le receipt contient la révision effectivement relue après l'action. Le
    // backend peut donc chaîner une mutation stable (nodeId/textSearch) sans
    // imposer une inspection systématique ; la borne de cinq actions et les
    // sélecteurs ordinaux restent ses garde-fous.
    return receiptOk(request, { receipt });
  } catch (error) {
    if (estEpoqueDepassee(error)) {
      // Le document a changé sous l'action. Les deux cas ne se répondent pas
      // pareil, et c'est tout l'objet de la troisième issue.
      const vide = SNAPSHOT_INDISPONIBLE;
      return receiptError(
        request,
        error.message + ' ' + (error.mutationDejaTentee
          ? 'Run canvas_inspect: it reads the document mounted now. Continue from what it shows and do not replay this action blindly: it may have been applied.'
          : 'Run canvas_inspect: it reads the document mounted now; then resend the action with targets from that read.'),
        {
          code: 'DOCUMENT_SESSION_CHANGED',
          capability: 'canvas.action.executor',
          data: {
            receipt: error.mutationDejaTentee
              ? inconclusiveAfterMutation({
                  intent: request.action.action,
                  snapshot: vide,
                  code: 'DOCUMENT_SESSION_CHANGED',
                  message: error.message,
                })
              : {
                  ...inconclusiveAfterMutation({
                    intent: request.action.action,
                    snapshot: vide,
                    code: 'DOCUMENT_SESSION_CHANGED',
                    message: error.message,
                  }),
                  // `aborted` : le document a changé AVANT toute mutation, donc
                  // rien n'a été écrit — c'est la définition même de l'issue
                  // (#990), et elle se distingue d'un échec d'écriture.
                  status: 'aborted' as const,
                  recovery: { action: 'reinspect' as const, reason: 'The document changed before any mutation was attempted; nothing was written.' },
                },
          },
        },
      );
    }
    const code = isCanvasSnapshotReadError(error) ? error.code : 'document_api_error';
    // Toujours porter le message SuperDoc ORIGINAL : « could not complete » nu a masqué
    // pendant 2 sessions que les throws venaient du re-read post-mutation sur blocs
    // fraîchement redlinés (conv 6b3d5ec4) — indiagnosticable sans l'erreur d'origine.
    const cause = error instanceof Error ? error.message.slice(0, 300) : String(error).slice(0, 300);
    return receiptError(
      request,
      `canvas_perform_action ${request.action.action} could not complete for requestId=${request.requestId}. ` +
      `SuperDoc error: ${cause}. ` +
      'IMPORTANT: the mutation may still have been APPLIED (SuperDoc can fail on the post-mutation re-read). ' +
      'Run canvas_inspect to check the actual document state before retrying — retrying blindly can duplicate the edit.',
      { code, capability: 'canvas.action.executor', data: { mutationMayHaveApplied: etatMutation.tentee } },
    );
  }
}

/**
 * Fait dire au reçu POURQUOI une écriture n'a pas eu lieu quand le refus passager
 * a épuisé sa borne (#1860).
 *
 * Sans ce geste, chaque exécuteur rend son reçu d'échec ordinaire — « rejected
 * by the document provider, re-inspect » — et l'agent relit un document que
 * personne n'a touché, sans jamais apprendre que réessayer suffisait.
 *
 * Trois cas, et ils ne se disent pas pareil :
 *
 * - **Rien n'a été écrit** (aucun appel mutant n'a abouti, révision inchangée) :
 *   le reçu devient `aborted` avec `recovery:'retry'`. C'est la définition même
 *   d'`aborted` — refusé avant toute mutation — et le courtier garde alors la
 *   portée de l'inspection : l'agent renvoie l'action sans relire.
 * - **Une partie est écrite** (une facette posée, la suivante refusée) : le reçu
 *   de l'exécuteur fait foi sur ce qui est acquis. On lui AJOUTE la cause, sans
 *   toucher à son statut ni à sa conduite — rejouer le lot dupliquerait.
 *   Exception : le retrait de bordures de `create_table` — rien n'est
 *   renvoyable, le reçu le dit au lieu de prescrire un renvoi.
 * - **Le reçu est `ok`** : l'écriture refusée était accessoire (habillage d'un
 *   tableau, retrait d'un saut de page hérité) et l'exécuteur a jugé l'effet
 *   demandé acquis. Le reçu reste `ok`, mais il ne reste pas muet : une
 *   vérification DÉCLARATIVE dit qu'une finition n'a pas été posée. Elle porte
 *   `passed:true` parce que le contrat partagé exige toutes les vérifications
 *   vertes sur un `ok` et n'y admet aucune erreur — même usage que
 *   `formatting_not_observable`.
 */
function nommerLEnregistrementEnCours(
  receipt: CanvasActionReceipt,
  bilan: BilanDesRefusPassagers,
): CanvasActionReceipt {
  if (bilan.issue !== 'bound_exceeded') return receipt;
  const cause = decrireLExportEnCours(bilan);
  if (receipt.status === 'ok') {
    return {
      ...receipt,
      verification: [
        ...receipt.verification,
        {
          name: 'accessory_write_skipped_export_in_progress',
          passed: true,
          message: `Declaration, not a proof: the requested effect is applied, but at least one accessory write of `
            + `${receipt.intent} (cosmetic finishing such as table dressing or inherited page-break removal) was NOT `
            + `applied — ${cause}. Do not replay the action; if the finishing matters, re-read the target and fix it `
            + 'with a dedicated action.',
        },
      ],
    };
  }
  if (bilan.appelsAboutis === 0 && receipt.preSnapshot.revision === receipt.postSnapshot.revision) {
    return abortedBeforeMutation({
      intent: receipt.intent,
      snapshot: receipt.preSnapshot,
      code: CODE_ENREGISTREMENT_EN_COURS,
      message: `canvas_perform_action ${receipt.intent} was not applied: ${cause}. Nothing was written and the `
        + 'inspected scope is intact. Resend the same action as is, without re-reading. If this code comes back, '
        + 'stop retrying in this turn and tell the lawyer the document could not be modified while it was exporting.',
      nextAction: 'Resend the same action once, without re-reading.',
      recovery: {
        action: 'retry',
        reason: 'Refused before any write while an export of the document was in progress: nothing was written and '
          + 'the inspected scope is intact. The same action can succeed once the export has finished.',
      },
    });
  }
  // ⚠️ Un retrait de bordures bloqué (`create_table borders:"none"`, #1861) n'a
  // RIEN à renvoyer : aucun verbe ne retire les bordures d'un tableau existant,
  // et « envoie ce qui manque » ne pourrait se traduire qu'en un second
  // `create_table` — un doublon dans l'acte. La cause est nommée, sans geste de
  // renvoi ; la conduite est celle du contrôle.
  if (receipt.verification.some((controle) => controle.name === CONTROLE_DU_RETRAIT_DES_BORDURES && !controle.passed)) {
    return {
      ...receipt,
      errors: [
        ...receipt.errors,
        {
          code: CODE_ENREGISTREMENT_EN_COURS,
          message: `canvas_perform_action ${receipt.intent}: the table IS in the document, filled, but it kept its `
            + `borders — ${cause}. There is nothing to resend: no action removes the borders of an existing table, `
            + 'and calling create_table again would add a second table. Tell the lawyer the table was created with '
            + 'its borders; they can be removed in Word.',
        },
      ],
    };
  }
  return {
    ...receipt,
    errors: [
      ...receipt.errors,
      {
        code: CODE_ENREGISTREMENT_EN_COURS,
        message: `canvas_perform_action ${receipt.intent} is incomplete: for part of it, ${cause}. What this `
          + 'receipt reports as applied IS in the document. Do not replay the whole action: re-read the affected '
          + 'target, then send only what is still missing.',
        nextAction: 'Re-read the affected target, then send only the part that is still missing.',
      },
    ],
  };
}

/**
 * Identité métier du document (uuid + titre depuis templateMeta), fournie par
 * WebSocketPool. La session routée reste exclusivement dans l'enveloppe et sa
 * garde d'époque.
 */
export type CanvasExecutionContext = {
  document?: { id: string | null; title: string | null };
  /** Actual executor entry, distinct from receiving or queuing a WS frame. */
  onExecutionStarted?: () => void;
  /** Actual writer chunks only; this carries no document content or receipt. */
  onUnitCompleted?: (progress: { completedUnits: number; totalUnits: number }) => void;
};

export type CanvasExecutionResources = {
  reviewEnabled: boolean;
  /**
   * Règle le régime de suivi du scope de CE document — la source que le bouton
   * de la barre affiche — pour `set_review_mode` (#1838). REQUIS, pour la même
   * raison que le résolveur ci-dessous : optionnel, la garde d'exécution
   * pourrait l'omettre sans qu'aucun type ne le signale.
   */
  setReviewEnabled: (reviewEnabled: boolean) => void;
  /**
   * L'état du peintre de l'éditeur de CE document, tenu par son scope : il
   * survit à l'appel qui l'a arrêté (`canvas-agent/peintreDeLEditeur.ts`).
   * REQUIS, pour la même raison que `setReviewEnabled`.
   */
  peintre: PeintreDuDocument;
  writer: CanvasDocumentWriter | null;
  /**
   * REQUIS, jamais optionnel : l'optionnalité « le temps de migrer les appelants »
   * a laissé la garde d'exécution l'omettre sans qu'aucun type ne le signale, et
   * `set_letterhead` refusait alors chaque pose. `null` = aucun résolveur monté.
   */
  namedLetterheadResolver: CanvasNamedLetterheadResolver | null;
  /**
   * L'hôte de CE document est-il parqué hors écran (#1586) ?
   *
   * ⚠️ Une FONCTION, lue à la demande : `isHostParked` passe par les ressources
   * DOM du scope, qui lèvent sans `document`. La lire d'office ferait échouer
   * chaque verbe hors navigateur, alors qu'un seul — `write_markdown replace` —
   * en a l'usage.
   */
  readonly isHostParked: () => boolean;
};

/**
 * Executes only the strict V2 envelope after the WebSocket lifecycle guard.
 *
 * Les deux dépendances passent par un objet NOMMÉ, jamais en positionnel : deux
 * paramètres facultatifs de même arité s'échangent en silence au premier ajout,
 * et l'erreur ne se voit qu'à l'exécution.
 */
export async function executeSuperDocCanvasRequest(
  instance: SuperDocInstance | null,
  request: CanvasRuntimeRequest,
  dependances: {
    garde: GardeDEpoque;
    contexte?: CanvasExecutionContext;
    resources: CanvasExecutionResources;
  },
): Promise<CanvasSuperDocResultPayload> {
  const {
    garde,
    contexte: context,
    resources,
  } = dependances;
  const doc = requireEditor(instance)?.doc;
  if (!doc) {
    return receiptError(
      request,
      `${request.tool} ${actionName(request)} failed because SuperDoc activeEditor.doc is unavailable for requestId=${request.requestId}; wait for the redaction canvas to mount, then run canvas_inspect again.`,
      { code: 'editor_unavailable', capability: 'editor.doc' },
    );
  }
  // The UI's `running` phase means this precise executor has begun. It does
  // not claim that the mutation is valid or applied; all such proof remains in
  // the eventual receipt and its postcondition checks.
  try {
    context?.onExecutionStarted?.();
  } catch {
    // A timeline observer is never allowed to alter the Canvas outcome.
  }
  if (request.tool === 'canvas_inspect') {
    return executeInspect(instance, doc, request, garde, context);
  }
  return executeAction(instance, doc, request, garde, resources, context);
}
